import React, { useMemo, useState, useEffect } from "react";
import axiosInstance from "../api/axiosInstance";
import { humanizeError } from "../utils/userFriendlyError";
import { useToast } from "../context/ToastContext";
import { formatAccountName } from "../utils/formatters";
import { NIGO_REASON_LABELS } from "../constants/householdConstants";

function ResolveNigoAccountsModal({
  isOpen,
  onClose,
  householdId,
  accounts,
  members,
  onSuccess,
}) {
  const { toast } = useToast();
  const [selectedIds, setSelectedIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const nigoAccounts = useMemo(
    () => (accounts || []).filter((a) => a?.status === "nigo"),
    [accounts],
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedIds([]);
      setErrorMessage(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const toggle = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const toggleAll = () => {
    if (selectedIds.length === nigoAccounts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(nigoAccounts.map((a) => a.id));
    }
  };

  const canSubmit = !submitting && selectedIds.length > 0;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const response = await axiosInstance.put(
        `/households/${householdId}/accounts/status`,
        {
          accounts: selectedIds.map((account_id) => ({
            account_id,
            status: "active",
          })),
        },
      );
      toast.success(
        response?.data?.message || "Selected accounts marked active.",
      );
      onSuccess?.(response.data);
      onClose();
    } catch (err) {
      setErrorMessage(humanizeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const allSelected =
    nigoAccounts.length > 0 && selectedIds.length === nigoAccounts.length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="resolve-nigo-title"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-emerald-100 bg-white shadow-2xl shadow-emerald-900/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-emerald-100 bg-gradient-to-r from-emerald-50/90 to-teal-50/50 px-5 py-4">
          <div>
            <h2
              id="resolve-nigo-title"
              className="text-lg font-bold text-emerald-950"
            >
              Resolve NIGO Accounts
            </h2>
            <p className="mt-0.5 text-xs font-medium text-emerald-800/70">
              Select the accounts you want to mark active.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-emerald-800/60 transition-colors hover:bg-white/80 hover:text-emerald-900"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 space-y-3">
            {nigoAccounts.length === 0 ? (
              <p className="text-sm italic text-gray-500">
                No NIGO accounts to resolve.
              </p>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {nigoAccounts.length} NIGO account
                    {nigoAccounts.length === 1 ? "" : "s"}
                  </span>
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="text-xs font-semibold text-emerald-700 hover:text-emerald-800"
                  >
                    {allSelected ? "Clear all" : "Select all"}
                  </button>
                </div>
                <div className="flex flex-col gap-2">
                  {nigoAccounts.map((account, idx) => {
                    const checked = selectedIds.includes(account.id);
                    return (
                      <label
                        key={account.id}
                        className={`flex items-start gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                          checked
                            ? "border-emerald-300 bg-emerald-50"
                            : "border-gray-200 hover:border-emerald-200"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(account.id)}
                          className="mt-0.5 h-4 w-4 rounded text-emerald-600 focus:ring-emerald-500"
                        />
                        <div className="flex flex-1 flex-col gap-0.5">
                          <span className="text-sm font-medium text-gray-900">
                            {formatAccountName(account, idx + 1, members || [])}
                          </span>
                          <div className="flex flex-wrap items-center gap-2 text-[11px]">
                            <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 font-semibold uppercase tracking-wide text-red-800">
                              NIGO
                            </span>
                            {account.sub_status && (
                              <span className="font-medium text-red-700">
                                {NIGO_REASON_LABELS[account.sub_status] ||
                                  account.sub_status}
                              </span>
                            )}
                            {account.notes && (
                              <span
                                className="truncate text-gray-600"
                                title={account.notes}
                              >
                                — {account.notes}
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </>
            )}

            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {errorMessage}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/60 px-5 py-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting && (
                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              Resolve Selected
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ResolveNigoAccountsModal;
